import { CheckOutlined } from '@mui/icons-material';
import { Badge, Box, Button, Grid, useMediaQuery, useTheme } from '@mui/material';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppCustomIcon from '../../../../../../app/features/icon/AppIcon';
import CAppDialog from '../../../../../../app/components/CAppDialog';
import investmentFilterLang from '../../langs/investmentFilterLang';
import investmentTypeArticleLang from '../../langs/investmentTypeArticleLang';
import investmentTypeDomainLang from '../../langs/investmentTypeDomainLang';
import IFund from '../../../../interfaces/fund/IFund';

import IInvestExpertiseFilters from '../../../../interfaces/expertise/IInvestExpertiseFilters';
import IInvestor from '../../../../interfaces/IInvestor';
import { TInvestFundSfdrTag } from '../../../../types/fund/TInvestFundSfdrTag';
import { TInvestFundDomainTag } from '../../../../types/fund/TInvestFundDomainTag';
import CFiltersFundSelect from './select/CFiltersFundSelect';
import CFiltersReportingPeriodSelect from './select/CFiltersReportingPeriodSelect';
import CFiltersInvestorSelect from './select/CFiltersInvestorSelect';
import CFiltersInvestmentYearSelect from './select/CFiltersInvestmentYearSelect';
import CFiltersDomainSelect from './select/CFiltersDomainSelect';
import CFiltersVintageSelect from './select/CFiltersVintageSelect';
import CFiltersSfrdSelect from './select/CFiltersSfrdSelect';
import { TInvestExpertiseTag } from '../../../../types/expertise/TInvestExpertiseTag';

import isoCountryCurrency from 'i18n-iso-countries';
import isoCountryCurrencyEN from 'i18n-iso-countries/langs/en.json';
import isoCountryCurrencyFR from 'i18n-iso-countries/langs/fr.json';
import langInvestWordLang from '../../../../langs/langInvestWordLang';
import IFundReportingPeriod from '../../../../interfaces/IFundReportingPeriod';
import CFiltersAssetClassSelect from './select/CFiltersAssetClassSelect';
import { TInvestFundAssetClassTag } from '../../../../types/fund/TInvestFundAssetClassTag';
import investTagAssetClassLang from '../../langs/investTagAssetClassLang';
import CFiltersThematiqueSelect from './select/CFiltersThematiqueSelect';
import investTagThematiqueLang from '../../langs/investTagThematiqueLang';
import { TInvestFundLabelTag } from '../../../../types/fund/TInvestFundLabelTag';
import CFiltersLabelSelect from './select/CFiltersLabelSelect';
import investTagLabelLang from '../../langs/investTagLabelLang';
import { TInvestFundRiskLevelTag } from '../../../../types/fund/TInvestFundRiskLevelTag';
import investTagRiskLevelLang from '../../langs/investTagRiskLevelLang';
import investTagPlacementDurationLang from '../../langs/investTagPlacementDurationLang';
import CFiltersPlacementDurationSelect from './select/CFiltersPlacementDurationSelect';
import CFiltersRiskLevelSelect from './select/CFiltersRiskLevelSelect';
import CFiltersClassificationAMFSelect from './select/CFiltersClassificationAMFSelect';
import { langLookup } from '../../../../../../app/utils/langLookup';

isoCountryCurrency.registerLocale(isoCountryCurrencyFR);
isoCountryCurrency.registerLocale(isoCountryCurrencyEN);

type CFiltersProps = {
	expertiseTag: TInvestExpertiseTag;
	allFunds: IFund[];
	filters: IInvestExpertiseFilters;
	onChange: (filters: IInvestExpertiseFilters, from: string) => void;
	onUpdate: (filterFunds: IFund[], reportingPeriod: string, investorIds: number[]) => void;
};

const CFilters = (props: CFiltersProps) => {
	const { expertiseTag, allFunds, filters, onChange, onUpdate } = props;
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const getFilterFundsForField = useCallback(
		(field: string | null = null) => {
			let filterFundsTmp = _.cloneDeep(allFunds);
			if (filters?.fundIds && field !== 'fundIds') {
				filterFundsTmp = filterFundsTmp.filter((x) => filters.fundIds?.includes(x.id));
			}
			if (filters?.assetClasses && filters?.assetClasses.length > 0 && field !== 'assetClasses') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => x?.assetClass && filters?.assetClasses?.includes(x?.assetClass),
				);
			}

			if (filters?.thematiques && filters?.thematiques.length > 0 && field !== 'thematiques') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => x?.thematic && filters?.thematiques?.includes(x?.thematic),
				);
			}
			if (filters?.labels && filters?.labels.length > 0 && field !== 'labels') {
				filterFundsTmp = filterFundsTmp.filter((x) => _.intersection(filters.labels, x.labels).length > 0);
			}
			if (
				filters?.classificationAMFs &&
				filters?.classificationAMFs.length > 0 &&
				field !== 'classificationAMFs'
			) {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => x.classificationAMF && filters.classificationAMFs?.includes(x.classificationAMF),
				);
			}
			if (
				filters?.placementDurations &&
				filters?.placementDurations.length > 0 &&
				field !== 'placementDurations'
			) {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => x.placementDuration && filters.placementDurations?.includes(x.placementDuration),
				);
			}
			if (filters?.riskLevels && filters?.riskLevels.length > 0 && field !== 'riskLevels') {
				filterFundsTmp = filterFundsTmp.filter((x) => x.risk && filters.riskLevels?.includes(x.risk));
			}
			if (filters?.reportingPeriod && field !== 'reportingPeriod') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) =>
						filters.reportingPeriod &&
						x.reportingPeriods?.map((y) => y.tag).includes(filters.reportingPeriod),
				);
			}
			if (filters?.investmentYear && field !== 'investmentYear') {
				filterFundsTmp = filterFundsTmp.filter((x) => x.investmentYear === filters.investmentYear);
			}
			if (filters?.domainTag && field !== 'domainTag') {
				filterFundsTmp = filterFundsTmp.filter((x) => x.domainTag === filters.domainTag);
			}
			if (filters?.vintage && field !== 'vintage') {
				filterFundsTmp = filterFundsTmp.filter((x) => x.vintage === filters.vintage);
			}
			if (typeof filters?.isRealized === 'boolean' && field !== 'isRealized') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => typeof x?.isRealized !== 'boolean' || x.isRealized === filters.isRealized,
				);
			}
			if (filters?.sfdr !== undefined && field !== 'sfdr') {
				filterFundsTmp = filterFundsTmp.filter((x) => x.sfdr === filters.sfdr);
			}
			if (filters?.assetRealizationStatusTag && field !== 'assetRealizationStatusTag') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) =>
						filters.assetRealizationStatusTag &&
						x.assets?.map((y) => y.investment?.reportingStatus).includes(filters.assetRealizationStatusTag),
				);
			}
			if (filters?.countryCode && field !== 'countryCode') {
				filterFundsTmp = filterFundsTmp.filter(
					(x) => filters.countryCode && x.assets?.map((y) => y.countryCode).includes(filters.countryCode),
				);
			}
			if (filters?.investorIds && field !== 'investorIds') {
				// has investor in fund and reporting period
				filterFundsTmp = filterFundsTmp.filter(
					(x) =>
						_.intersection(
							x.investors?.map((y) => y.company.id),
							filters.investorIds,
						).length > 0 &&
						_.intersection(_.flatten(x.reportingPeriods?.map((rp) => rp.investorIds)), filters.investorIds)
							.length > 0,
				);
				// has investor in reporting period => filter reporting period
				filterFundsTmp.forEach((filterFundTmp) => {
					filterFundTmp.reportingPeriods = filterFundTmp.reportingPeriods?.filter(
						(rp) => _.intersection(rp.investorIds, filters.investorIds).length > 0,
					);
				});
			}
			return filterFundsTmp;
		},
		[allFunds, filters],
	);

	const [filterFunds, setFilterFunds] = useState<IFund[]>([]);
	useEffect(() => {
		setFilterFunds(getFilterFundsForField());
	}, [allFunds, filters, getFilterFundsForField]);

	useEffect(() => {
		onUpdate(filterFunds, filters.reportingPeriod as string, filters.investorIds as number[]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds, filters]);

	const [nbMoreFilters, setNbMoreFilters] = useState<number>(0);
	useEffect(() => {
		let nbMoreFiltersTmp = 0;
		if (filters) {
			if ((filters.investorIds?.length ?? 0) > 0) {
				nbMoreFiltersTmp += 1;
			}
			if (filters.vintage !== null && filters.vintage !== undefined) {
				nbMoreFiltersTmp += 1;
			}
			if (filters.sfdr !== null && filters.sfdr !== undefined) {
				nbMoreFiltersTmp += 1;
			}
			if (filters.labels && filters.labels.length > 0) {
				nbMoreFiltersTmp += 1;
			}
			if ((filters.placementDurations?.length || 0) > 0) {
				nbMoreFiltersTmp += 1;
			}
			if ((filters.riskLevels?.length || 0) > 0) {
				nbMoreFiltersTmp += 1;
			}
			if (
				['private_equity', 'venture_capital'].includes(expertiseTag) &&
				filters.investmentYear !== null &&
				filters.investmentYear !== undefined
			) {
				nbMoreFiltersTmp += 1;
			}
		}
		setNbMoreFilters(nbMoreFiltersTmp);
	}, [expertiseTag, filters]);

	// Options sub portfolio
	const [optionsInvestors, setOptionsInvestors] = useState<IInvestor[]>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsInvestors(
				_.uniqBy(
					_.flatten(
						getFilterFundsForField('investorIds').map((x) => _.flatten(x.investors?.map((i) => i.company))),
					),
					'id',
				),
			);
		}
	}, [filterFunds, getFilterFundsForField]);

	// Options sur les années d'investissement (on utilise les années des fonds)
	const [optionsInvestmentYears, setOptionsInvestmentYears] = useState<{ id: number; name: string }[]>([]);
	useEffect(() => {
		if (filterFunds && getFilterFundsForField('investmentYear') && t) {
			let optionsTmp: { id: number; name: string }[] = [];
			if (filterFunds.some((x) => x.investmentYear === null)) {
				optionsTmp.push({
					id: -1,
					name: t(investmentFilterLang.not_call_yet.tag),
				});
			}
			optionsTmp = _.union(
				optionsTmp,
				_.uniqBy(
					getFilterFundsForField('investmentYear')
						.filter((x) => x.investmentYear !== null)
						.map((x) => x.investmentYear)
						.map((x) => ({
							id: x as number,
							name: (x as number).toString(),
						})),
					'id',
				),
			);
			setOptionsInvestmentYears(_.orderBy(optionsTmp, ['id'], ['desc']));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les périodes de reporting
	const [optionsReportingPeriods, setReportingPeriods] = useState<IFundReportingPeriod[]>([]);
	useEffect(() => {
		if (filterFunds && filters && onChange && getFilterFundsForField('reportingPeriod') && t) {
			const optionsReportingPeriodsTmp: IFundReportingPeriod[] = [
				{
					tag: null,
					name: t(investmentFilterLang.value_as_of_today.tag) + ' - ' + t(langInvestWordLang.estimated.tag),
				},
			];
			const filterFundsForRepportingPeriod = getFilterFundsForField('reportingPeriod');
			const reportingPeriodsTmp = _.orderBy(
				_.union(
					optionsReportingPeriodsTmp,
					_.uniqBy(
						_.flatten(
							filterFundsForRepportingPeriod.map((x) =>
								(x.reportingPeriods ?? []).map((rp) => ({
									...rp,
									name: rp.name + ' - ' + t(langInvestWordLang.audited.tag),
								})),
							),
						),
						'tag',
					),
				),
				['tag'],
				['desc'],
			);

			// FILTER REPORTING PERIOD WHEN ONE OF REPORTING PERIOD HAS NAV
			// => On regroupe par tag toutes les périodes des fonds affichés dans le filtre
			const allPeriodsGroupByTag = _.groupBy(
				_.flatten(filterFundsForRepportingPeriod.map((x) => x.reportingPeriods ?? [])),
				'tag',
			);
			// On parcourt toutes les périodes une par une pour filtrer celles qui ont une NAV
			// => On garde les tags des périodes uniquement s'ils ont au moins une NAV
			const keepTags: string[] = [];
			Object.keys(allPeriodsGroupByTag).forEach((tag) => {
				if (allPeriodsGroupByTag[tag].filter((x) => x.hasNav).length > 0) {
					keepTags.push(tag);
				}
			});

			// => On garde uniquement la période avec le tag à null (As of today) et celles qui sont dans la liste des tags à garder
			setReportingPeriods(reportingPeriodsTmp.filter((x) => x.tag === null || keepTags.includes(x.tag)));

			// => Si on a déjà filtré sur reportingPeriod et que la reportingPeriod n'est pas incluse dans la liste des périodes à conserver, on reset le filtre automatiquement
			if (filters.reportingPeriod && !keepTags.includes(filters.reportingPeriod)) {
				onChange(
					{
						...filters,
						reportingPeriod: null,
					},
					'setReportingPeriods',
				);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les Vintages
	const [optionsVintages, setOptionsVintage] = useState<{ id: number; name: string }[] | null>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsVintage(
				_.orderBy(
					_.uniqBy(
						getFilterFundsForField('vintage')
							.filter((x) => x.vintage !== null)
							.map((x) => x.vintage)
							.filter((x) => x)
							.map((x) => ({
								id: x as number,
								name: (x as number)?.toString(),
							})),
						'id',
					),
					['id'],
					['desc'],
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les SFRD
	const [optionsSFRD, setOptionsSFRD] = useState<{ id: TInvestFundSfdrTag; name: string }[] | null>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsSFRD(
				_.uniqBy(
					getFilterFundsForField('sfdr')
						.map((x) => x.sfdr)
						.filter((x) => x)
						.map((x) => ({
							id: x as TInvestFundSfdrTag,
							name: (x && langLookup(investmentTypeArticleLang, x)
								? t(langLookup(investmentTypeArticleLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les assetClasses
	const [optionsAssetClasses, setOptionsAssetClasses] = useState<
		{ id: TInvestFundAssetClassTag; name: string }[] | null
	>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsAssetClasses(
				_.uniqBy(
					getFilterFundsForField('assetClasses')
						.map((x) => x.assetClass)
						.filter((x) => x)
						.map((x) => ({
							id: x as TInvestFundAssetClassTag,
							name: (x && langLookup(investTagAssetClassLang, x)
								? t(langLookup(investTagAssetClassLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les thematiques
	const [optionsThematiques, setOptionsThematiques] = useState<{ id: string; name: string }[] | null>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsThematiques(
				_.uniqBy(
					getFilterFundsForField('thematiques')
						.map((x) => x.thematic)
						.filter((x) => x)
						.map((x) => ({
							id: x as string,
							name: (x && langLookup(investTagThematiqueLang, x)
								? t(langLookup(investTagThematiqueLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les classificationAMFs
	const [optionsClassificationAMFs, setOptionsClassificationAMFs] = useState<{ id: string; name: string }[] | null>(
		[],
	);
	useEffect(() => {
		if (filterFunds) {
			setOptionsClassificationAMFs(
				_.uniqBy(
					getFilterFundsForField('classificationamfs')
						.map((x) => x.classificationAMF)
						.filter((x) => x)
						.map((x) => ({
							id: x as string,
							name: x as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les placementDurations
	const [optionsPlacementDurations, setOptionsPlacementDurations] = useState<{ id: string; name: string }[] | null>(
		[],
	);
	useEffect(() => {
		if (filterFunds) {
			setOptionsPlacementDurations(
				_.uniqBy(
					getFilterFundsForField('placementDurations')
						.map((x) => x.placementDuration)
						.filter((x) => x)
						.map((x) => ({
							id: x as string,
							name: (x && langLookup(investTagPlacementDurationLang, x)
								? t(langLookup(investTagPlacementDurationLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les riskLevels
	const [optionsRiskLevels, setOptionsRiskLevels] = useState<{ id: TInvestFundRiskLevelTag; name: string }[] | null>(
		[],
	);
	useEffect(() => {
		if (filterFunds) {
			setOptionsRiskLevels(
				_.orderBy(
					_.uniqBy(
						getFilterFundsForField('riskLevels')
							.map((x) => x.risk)
							.filter((x) => x)
							.map((x) => ({
								id: x as TInvestFundRiskLevelTag,
								name: (x && langLookup(investTagRiskLevelLang, x)
									? t(langLookup(investTagRiskLevelLang, x)?.tag ?? '')
									: x) as string,
							})),
						'id',
					),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les labels
	const [optionsLabels, setOptionsLabels] = useState<{ id: TInvestFundLabelTag; name: string }[] | null>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsLabels(
				_.uniqBy(
					_.flatten(getFilterFundsForField('labels').map((x) => x.labels))
						.filter((x) => x)
						.map((x) => ({
							id: x as TInvestFundLabelTag,
							name: (x && langLookup(investTagLabelLang, x)
								? t(langLookup(investTagLabelLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	// Options sur les domaines
	const [optionsDomains, setOptionsDomains] = useState<{ id: TInvestFundDomainTag; name: string }[] | null>([]);
	useEffect(() => {
		if (filterFunds) {
			setOptionsDomains(
				_.uniqBy(
					getFilterFundsForField('domainTag')
						.map((x) => x.domainTag)
						.map((x) => ({
							id: x as TInvestFundDomainTag,
							name: (x && langLookup(investmentTypeDomainLang, x)
								? t(langLookup(investmentTypeDomainLang, x)?.tag ?? '')
								: x) as string,
						})),
					'id',
				),
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filterFunds]);

	const [openFilters, setOpenFilters] = useState(false);

	return (
		<>
			{filterFunds && expertiseTag ? (
				<Box>
					<Grid
						container
						rowSpacing={0}
						columnSpacing={2}
						alignItems="center"
						style={{ marginTop: '0px', marginBottom: '14px' }}
					>
						<Grid item container sm={10} xs={10} rowSpacing={0} columnSpacing={2} alignItems="center">
							{expertiseTag === 'private_credit' ? (
								<>
									<Grid item xs={12} sm={4}>
										<CFiltersFundSelect
											allFunds={allFunds}
											filterFunds={filterFunds}
											filters={filters}
											getFilterFundsForField={getFilterFundsForField}
											onChange={(val) => onChange(val, 'filterFunds')}
											label={t(investmentFilterLang.search_for_funds_private_credit.tag)}
											multipleRenderLabelLangTag={investmentFilterLang.x_funds_private_credit.tag}
										/>
									</Grid>
									<Grid item sm={3} sx={{ display: { xs: 'none', sm: 'block' } }}>
										<CFiltersReportingPeriodSelect
											optionsReportingPeriods={optionsReportingPeriods}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsReportingPeriods')}
										/>
									</Grid>
									<Grid item sm={3} sx={{ display: { xs: 'none', sm: 'block' } }}>
										<CFiltersInvestmentYearSelect
											optionsInvestmentYears={optionsInvestmentYears}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsInvestmentYears')}
										/>
									</Grid>

									<Grid item sm={2} sx={{ display: { xs: 'none', sm: 'block' } }}>
										<CFiltersDomainSelect
											optionsDomains={optionsDomains ?? []}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsDomains')}
										/>
									</Grid>
								</>
							) : expertiseTag === 'venture_capital' || expertiseTag === 'private_equity' ? (
								<>
									<Grid item xs={10} sm={8}>
										<CFiltersFundSelect
											allFunds={allFunds}
											filterFunds={filterFunds}
											filters={filters}
											getFilterFundsForField={getFilterFundsForField}
											onChange={(val) => onChange(val, 'filterFunds')}
											label={t(investmentFilterLang.search_for_funds_venture_capital.tag)}
											multipleRenderLabelLangTag={
												investmentFilterLang.x_funds_venture_capital.tag
											}
										/>
									</Grid>
									<Grid item sm={4} sx={{ display: { xs: 'none', sm: 'block' } }}>
										<CFiltersReportingPeriodSelect
											optionsReportingPeriods={optionsReportingPeriods}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsReportingPeriods')}
										/>
									</Grid>
								</>
							) : expertiseTag === 'listed_assets' ? (
								<>
									<Grid item xs={12} sm={4}>
										<CFiltersFundSelect
											allFunds={allFunds}
											filterFunds={filterFunds}
											filters={filters}
											getFilterFundsForField={getFilterFundsForField}
											onChange={(val) => onChange(val, 'filterFunds')}
											label={t(investmentFilterLang.search_for_funds_listed_assets.tag)}
											multipleRenderLabelLangTag={investmentFilterLang.x_funds_listed_assets.tag}
										/>
									</Grid>
									{!isMobile ? (
										<>
											<Grid item xs={12} sm={4}>
												<CFiltersClassificationAMFSelect
													optionsClassificationAMFs={optionsClassificationAMFs ?? []}
													filters={filters}
													onChange={(val) => onChange(val, 'optionsClassificationAMFs')}
													label={t(investmentFilterLang.search_for_classification_amfs.tag)}
													multipleRenderLabelLangTag={
														investmentFilterLang.x_classification_amfs.tag
													}
												/>
											</Grid>
											<Grid item xs={12} sm={4}>
												<CFiltersThematiqueSelect
													optionsThematiques={optionsThematiques ?? []}
													filters={filters}
													onChange={(val) => onChange(val, 'thematiques')}
													label={t(investmentFilterLang.search_for_thematiques.tag)}
													multipleRenderLabelLangTag={investmentFilterLang.x_thematiques.tag}
												/>
											</Grid>
										</>
									) : (
										<></>
									)}
								</>
							) : (
								<>{expertiseTag} filters not found</>
							)}
						</Grid>
						<Grid item sm={2} xs={2}>
							<Badge
								color="primary"
								variant="dot"
								sx={{
									width: '100%',
									'.MuiBadge-badge': { width: '10px', height: '10px' },
								}}
								invisible={nbMoreFilters > 0 ? false : true}
							>
								{!isMobile ? (
									<Button
										className="btn-more-filters"
										size="small"
										onClick={() => setOpenFilters(true)}
										startIcon={<AppCustomIcon tag="more_filters" />}
										color="secondary"
										variant="contained"
										fullWidth
									>
										{t(investmentFilterLang.btn_more_filters.tag)}
									</Button>
								) : (
									<Button
										className="btn-more-filters"
										size="small"
										onClick={() => setOpenFilters(true)}
										color="secondary"
										variant="contained"
										fullWidth
										sx={{}}
									>
										<AppCustomIcon tag="more_filters" />
									</Button>
								)}
							</Badge>
						</Grid>
					</Grid>
					<CAppDialog
						open={openFilters}
						onClose={() => setOpenFilters(false)}
						title={t(investmentFilterLang.dialog_filters_title.tag)}
						actions={
							<Grid container spacing={3} alignItems="center" justifyContent="center">
								<Grid item sm={3} style={{ textAlign: 'center' }}>
									<Button
										id="btn-apply-filters"
										variant="contained"
										color="secondary"
										fullWidth
										startIcon={<CheckOutlined />}
										onClick={() => setOpenFilters(false)}
									>
										{t(investmentFilterLang.dialog_btn_apply.tag)}
									</Button>
								</Grid>
							</Grid>
						}
					>
						<>
							<Grid container spacing={3}>
								<Grid item sm={6} xs={12}>
									<CFiltersFundSelect
										allFunds={allFunds}
										filterFunds={filterFunds}
										filters={filters}
										getFilterFundsForField={getFilterFundsForField}
										onChange={(val) => onChange(val, 'filterFunds')}
										label={t(
											expertiseTag === 'private_credit' || expertiseTag === 'listed_assets'
												? investmentFilterLang.search_for_funds_private_credit.tag
												: expertiseTag === 'venture_capital' ||
												  expertiseTag === 'private_equity'
												? investmentFilterLang.search_for_funds_venture_capital.tag
												: investmentFilterLang.search_for_funds_venture_capital.tag,
										)}
										multipleRenderLabelLangTag={
											expertiseTag === 'private_credit' || expertiseTag === 'listed_assets'
												? investmentFilterLang.x_funds_private_credit.tag
												: expertiseTag === 'venture_capital' ||
												  expertiseTag === 'private_equity'
												? investmentFilterLang.x_funds_venture_capital.tag
												: investmentFilterLang.x_funds_venture_capital.tag
										}
									/>
								</Grid>
								{['listed_assets'].includes(expertiseTag) ? (
									<Grid item sm={6} xs={12}>
										<CFiltersThematiqueSelect
											optionsThematiques={optionsThematiques ?? []}
											filters={filters}
											onChange={(val) => onChange(val, 'thematiques')}
											label={t(investmentFilterLang.search_for_thematiques.tag)}
											multipleRenderLabelLangTag={investmentFilterLang.x_thematiques.tag}
										/>
									</Grid>
								) : (
									<></>
								)}
								{['private_credit', 'venture_capital', 'private_equity'].includes(expertiseTag) ? (
									<Grid item sm={6} xs={12}>
										<CFiltersInvestorSelect
											optionsInvestors={optionsInvestors}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsInvestors')}
										/>
									</Grid>
								) : (
									<></>
								)}
								{['private_credit', 'venture_capital', 'private_equity'].includes(expertiseTag) ? (
									<Grid item sm={4} xs={12}>
										<CFiltersReportingPeriodSelect
											optionsReportingPeriods={optionsReportingPeriods}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsReportingPeriods')}
										/>
									</Grid>
								) : (
									<></>
								)}
								{['private_credit', 'venture_capital', 'private_equity'].includes(expertiseTag) ? (
									<Grid item sm={4} xs={12}>
										<CFiltersInvestmentYearSelect
											optionsInvestmentYears={optionsInvestmentYears}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsInvestmentYears')}
										/>
									</Grid>
								) : (
									<></>
								)}

								{['private_credit'].includes(expertiseTag) ? (
									<Grid item sm={4} xs={12}>
										<CFiltersDomainSelect
											optionsDomains={optionsDomains ?? []}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsDomains')}
										/>
									</Grid>
								) : (
									<></>
								)}
								{['private_credit', 'venture_capital', 'private_equity'].includes(expertiseTag) ? (
									<Grid item sm={4} xs={12}>
										<CFiltersVintageSelect
											optionsVintages={optionsVintages ?? []}
											filters={filters}
											onChange={(val) => onChange(val, 'optionsVintages')}
										/>
									</Grid>
								) : (
									<></>
								)}

								{['listed_assets'].includes(expertiseTag) ? (
									<>
										<Grid item sm={4} xs={12}>
											<CFiltersClassificationAMFSelect
												optionsClassificationAMFs={optionsClassificationAMFs ?? []}
												filters={filters}
												onChange={(val) => onChange(val, 'optionsClassificationAMFs')}
												label={t(investmentFilterLang.search_for_classification_amfs.tag)}
												multipleRenderLabelLangTag={
													investmentFilterLang.x_classification_amfs.tag
												}
											/>
										</Grid>
										<Grid item sm={4} xs={12}>
											<CFiltersAssetClassSelect
												optionsAssetClasses={optionsAssetClasses ?? []}
												filters={filters}
												onChange={(val) => onChange(val, 'assetClasses')}
												label={t(investmentFilterLang.search_for_asset_classes.tag)}
												multipleRenderLabelLangTag={investmentFilterLang.x_asset_classes.tag}
											/>
										</Grid>
										<Grid item sm={4} xs={12}>
											<CFiltersPlacementDurationSelect
												optionsPlacementDurations={optionsPlacementDurations ?? []}
												filters={filters}
												onChange={(val) => onChange(val, 'placementDurations')}
												label={t(investmentFilterLang.search_for_placement_durations.tag)}
												multipleRenderLabelLangTag={
													investmentFilterLang.x_placement_durations.tag
												}
											/>
										</Grid>
										<Grid item sm={4} xs={12}>
											<CFiltersRiskLevelSelect
												optionsRiskLevels={optionsRiskLevels ?? []}
												filters={filters}
												onChange={(val) => onChange(val, 'riskLevels')}
												label={t(investmentFilterLang.search_for_risk_levels.tag)}
												multipleRenderLabelLangTag={investmentFilterLang.x_risk_levels.tag}
											/>
										</Grid>
										<Grid item sm={4} xs={12}>
											<CFiltersLabelSelect
												optionsLabels={optionsLabels ?? []}
												filters={filters}
												onChange={(val) => onChange(val, 'labels')}
												label={t(investmentFilterLang.search_for_labels.tag)}
												multipleRenderLabelLangTag={investmentFilterLang.x_labels.tag}
											/>
										</Grid>
									</>
								) : (
									<></>
								)}

								<Grid item sm={4} xs={12}>
									<CFiltersSfrdSelect
										optionsSFRD={optionsSFRD ?? []}
										filters={filters}
										onChange={(val) => onChange(val, 'optionsSFRD')}
									/>
								</Grid>
							</Grid>
						</>
					</CAppDialog>
				</Box>
			) : (
				''
			)}
		</>
	);
};

export default CFilters;
